import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useId, useState } from "react";
import { TextInput } from "../../../components/ui/form";
import { useAnalytics } from "../../../lib/analytics";
import { useClientTranslations } from "../../../lib/i18n/client";
import type { OnboardingAnswers } from "../../../types/onboarding";
import {
	validateBirthYear,
	type YearValidationErrorCode,
} from "../../../utils/dateValidation";
import styles from "./SetupProfileDetailsQuestion.module.css";

interface SetupProfileDetailsQuestionProps {
	answers: OnboardingAnswers;
	onInputChange: (field: string, value: string | number | string[]) => void;
	showValidation?: boolean;
}

export function SetupProfileDetailsQuestion({
	answers,
	onInputChange,
	showValidation = false,
}: SetupProfileDetailsQuestionProps) {
	const { t, direction } = useClientTranslations("onboarding");
	const { logEvent } = useAnalytics();
	const yearInputId = useId();
	const genderGroupId = useId();
	const [showYearInfo, setShowYearInfo] = useState(false);
	const [showGenderInfo, setShowGenderInfo] = useState(false);
	const [yearValidationErrorCode, setYearValidationErrorCode] =
		useState<YearValidationErrorCode | null>(null);

	const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		// Only allow 4 digits
		if (value.length <= 4 && /^\d*$/.test(value)) {
			onInputChange("yearOfBirth", value);

			// Validate year if it's a complete 4-digit year
			if (value.length === 4) {
				const validation = validateBirthYear(value);
				if (!validation.isValid) {
					setYearValidationErrorCode(validation.errorCode || "invalid");
				} else {
					setYearValidationErrorCode(null);
					logEvent("setup_profile_year_entered", undefined, {
						field_name: "yearOfBirth",
						question_type: "setup-profile-details",
						has_content: "true",
						year_value: value,
					});
				}
			} else {
				// Clear validation error when user is still typing
				setYearValidationErrorCode(null);
			}
		}
	};

	const handleGenderSelect = (gender: string) => {
		onInputChange("gender", gender);
		logEvent("setup_profile_gender_selected", undefined, {
			field_name: "gender",
			question_type: "setup-profile-details",
			selected_value: gender,
		});
	};

	// Check if fields have errors when validation is shown
	// Also validate incomplete years when showValidation is true
	let finalYearError = yearValidationErrorCode;
	if (showValidation && answers.yearOfBirth) {
		const validation = validateBirthYear(answers.yearOfBirth, false);
		if (!validation.isValid) {
			finalYearError = validation.errorCode || "invalid";
		}
	}

	const yearHasError =
		(showValidation && !answers.yearOfBirth) || !!finalYearError;
	const genderHasError = showValidation && !answers.gender;

	// Get localized error message
	const yearErrorMessage = finalYearError
		? t(`setupProfileDetails.yearValidation.${finalYearError}`)
		: null;

	const genderOptions = [
		{ value: "Female", label: t("setupProfileDetails.genderOptions.female") },
		{ value: "Male", label: t("setupProfileDetails.genderOptions.male") },
		{
			value: "Non-binary",
			label: t("setupProfileDetails.genderOptions.nonBinary"),
		},
	];

	return (
		<div className={styles.container} dir={direction}>
			<div className={styles.inputCard}>
				<div className={styles.inputGroup}>
					<div className={styles.labelRow}>
						<label
							htmlFor={yearInputId}
							className={`${styles.label} ${yearHasError ? styles.labelError : ""}`}
						>
							{t("setupProfileDetails.yearQuestion")}
						</label>
						<button
							type="button"
							className={styles.infoButton}
							onClick={() => setShowYearInfo(!showYearInfo)}
							aria-label="Information about birth year"
						>
							<InformationCircleIcon className={styles.infoIcon} />
						</button>
					</div>
					<TextInput
						id={yearInputId}
						variant="onboarding"
						placeholder={t("setupProfileDetails.yearPlaceholder")}
						value={answers.yearOfBirth || ""}
						onChange={handleYearChange}
						className={`${styles.input} ${yearHasError ? styles.inputError : ""}`}
						maxLength={4}
					/>
					{yearErrorMessage && (
						<div className={styles.errorMessage}>{yearErrorMessage}</div>
					)}
					{showYearInfo && (
						<div className={styles.infoTooltip}>
							{t("setupProfileDetails.yearInfo")}
						</div>
					)}
				</div>

				<div className={styles.inputGroup}>
					<div className={styles.labelRow}>
						<label
							htmlFor={genderGroupId}
							className={`${styles.label} ${genderHasError ? styles.labelError : ""}`}
						>
							{t("setupProfileDetails.genderQuestion")}
						</label>
						<button
							type="button"
							className={styles.infoButton}
							onClick={() => setShowGenderInfo(!showGenderInfo)}
							aria-label="Information about gender"
						>
							<InformationCircleIcon className={styles.infoIcon} />
						</button>
					</div>
					{/* biome-ignore lint/a11y/useSemanticElements: Using div with role="group" to maintain consistent styling with other components */}
					<div className={styles.genderButtons} role="group" id={genderGroupId}>
						{genderOptions.map((option) => (
							<button
								key={option.value}
								type="button"
								onClick={() => handleGenderSelect(option.value)}
								className={`${styles.genderButton} ${
									answers.gender === option.value
										? styles.genderButtonSelected
										: ""
								} ${genderHasError ? styles.genderButtonError : ""}`}
							>
								{option.label}
							</button>
						))}
					</div>
					{showGenderInfo && (
						<div className={styles.infoTooltip}>
							{t("setupProfileDetails.genderInfo")}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
